import React, { Suspense, useEffect } from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Route } from 'react-router-dom';
// eslint-disable-next-line
import { v4 as uuidv4 } from 'uuid'; // eslint-disable-next-line
import { getLocalLanguage } from 'theSource/features/Login/utils/utils';
import 'orbit-dist/orbit/stylesheets/bootstrap_orbit.css';
import 'orbit-dist/orbit/stylesheets/application.css';
import 'orbit-dist/orbit/javascripts/web_components.js';
import { ScrollToTop } from 'components/ScrollToTop';
import { ROUTES } from 'theSource/app/routes';
import {
  ErrorBoundary,
  Loader,
  PageLayout,
  PageNotFound,
  PrivateOutlet,
  PublicOutlet,
  RoutesInternal,
  Snackbar,
} from 'theSource/components';
import {
  AccessDenied,
  Benefits,
  ChangePassword,
  Contacts,
  FamilyMembers,
  ForgotPassword,
  TermsDisplay,
  Home,
  Inbox,
  Login,
  Profile,
  selectIsAuthenticated,
  setTermsOfUseAccepted,
  Settings,
  CompanyCategories,
  LegalDocuments,
  LicensesPage,
  PrivacyPolicyDisplay,
  setIsAdmin,
  LumosChangePassword,
  selectSourceEnabled,
  setSourceEnabled,
  setLumosEnabled,
  TermsOfUse,
  selectIsAdmin,
  IdCards,
  setOldEnrollmentUrl,
  setHomeEnabled,
  setMfaEnabled,
  setMfaRequired,
  setMfaConfigured,
} from 'theSource/features';
import { defaultTheme, getTheme } from 'theSource/theme';
import {
  useAuthenticationMutation,
  useBenefitsQuery,
  useCompanyManifestQuery,
  useConfigurationQuery,
  useContentSettingsQuery,
  useSubscriberQuery,
  useCompanySettingsQuery,
  useAdminRedirectQuery,
  ConfigurationResponseDto,
} from 'theSource/queries';
import {
  chatBotOrgId,
  chatBotSessionId,
  getCompanyName,
  getNotificationsPermission,
  isNativeAuthSupported,
  isTermsOfUseAgreementValidInLocalStorage,
  loadChatBot,
  chatBotEntity,
} from 'theSource/utils';
import { DEFAULT_CACHE_THRESHOLD } from 'theSource/features/TermsOfUse/utils';
import {
  setIsSupported,
  setUsername,
  showNavigationMenus,
  hideNavigationMenus,
  selectNavigationVisible,
  setEnrollmentApiError,
} from 'theSource/services';
import { setDownloadPrompt } from 'theSource/features/PostLoginDownloadPrompt/services';
import { BeforeInstallPromptEvent } from 'theSource/features/PostLoginDownloadPrompt/types';
import { SUBSCRIBER_LOGIN_RESPONSE_CODE } from 'theSource/features/AccessDenied/utils';
import { Enrollment } from 'enrollment/Enrollment';
import { useUserSpecificQuery } from './theSource/queries/useUserSpecificQuery';
import {
  AddYourDocuments,
  ClickToChat,
  EOI,
  MyIdCards,
  ReportLifeEvent,
} from 'enrollment/features';
import { MFA } from 'theSource/features/MFA';
import { HomePage } from 'features/HomePage/HomePage';
import {
  BenefitInformation,
  ViewAllBenefits,
} from 'features/HomePage/components/BenefitsInfoPageContent';
import { MyProfile } from 'enrollment/features/MyProfile';
import { MyFamily } from 'enrollment/features/MyFamily';
import { MyContacts } from 'enrollment/features/MyContacts';
import { MyCompany } from 'enrollment/features/MyCompany';
import { setPerPayPeriodText } from 'enrollment/services';
import { MyBeneficiaries } from 'features/MyBeneficiaries';
import { AcaDocuments, ConfigureMFA } from 'features';
import { useAccountDetailsQuery } from './queries';
import { ToastDialogProvider } from 'enrollment/components/Shared/ToastDialogContext/ToastDialogContext';

declare let pendo: any;

export const App = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const navigationVisible = useSelector(selectNavigationVisible);
  const sourceEnabled = useSelector(selectSourceEnabled);
  const isAdmin = useSelector(selectIsAdmin);

  const { configuration, isLoadingConfiguration } = useConfigurationQuery();
  const { isAuthenticating, authenticate } = useAuthenticationMutation();
  const { admin, isLoadingAdmin, isFetchingAdmin } = useAdminRedirectQuery();
  const { subscriber, isLoadingSubscriber, isFetchingSubscriber } =
    useSubscriberQuery({
      restrictAdmin: true,
    });

  const {
    contentSettings,
    isLoadingContentSettings,
    isFetchingContentSettings,
  } = useContentSettingsQuery({
    enabled: Boolean(isAuthenticated && configuration?.apiHost),
  });

  const companyName = getCompanyName();

  // To get the organization license settings
  const { companySettings, isLoading: isCompanySettingsLoading } =
    useCompanySettingsQuery({
      enabled: Boolean(configuration?.apiHost),
    });

  const { data: manifestData, isLoading: isLoadingManifestData } =
    useCompanyManifestQuery({
      enabled: companySettings?.data.source_license || false,
    });

  const lumosEnabled = companySettings?.data.lumos_feature_toggle || false;
  const homeEnabled =
    companySettings?.data.lumos_home_page_feature_toggle || false;

  const { isLoading: isLoadingBenefitsData } = useBenefitsQuery({
    enabled: Boolean(isAuthenticated && configuration?.apiHost),
  });

  const { isLoading: isLoadingUserSpecificData } = useUserSpecificQuery({
    enabled: Boolean(isAuthenticated && configuration?.apiHost),
  });

  const { data: accountDetails } = useAccountDetailsQuery({
    enabled: Boolean(isAuthenticated && configuration?.apiHost),
  });

  useEffect(() => {
    // Set the initial language from localStorage
    const preferredLanguage = getLocalLanguage() || 'en';
    document.documentElement.lang = preferredLanguage; // Set the <html> lang attribute

    // Watch for changes to the preferred language in localStorage
    const handleStorageChange = () => {
      const updatedLanguage = getLocalLanguage() || 'en';
      document.documentElement.lang = updatedLanguage; // Update the <html> lang attribute
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      chatBotEntity({ subscriberId: uuidv4() });
      chatBotSessionId({ sessionId: null });
      chatBotOrgId({ orgId: null });
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (companySettings) {
      dispatch(setSourceEnabled(companySettings.data.source_license));
      dispatch(setLumosEnabled(companySettings.data.lumos_feature_toggle));
      dispatch(
        setHomeEnabled(companySettings.data.lumos_home_page_feature_toggle)
      );
      if (
        companySettings.data.lumos_home_page_feature_toggle &&
        configuration?.enrollmentUrl
      ) {
        dispatch(setOldEnrollmentUrl(configuration?.enrollmentUrl));
      }
    }
    // eslint-disable-next-line
  }, [dispatch, companySettings]);

  useEffect(() => {
    if (companySettings && companySettings.data.chat_bot) {
      loadChatBot({
        chatBotApiUrl: String(configuration?.chatBotUrl),
        benApiUrl: String(configuration?.apiHost),
        identity: isAdmin || !subscriber ? uuidv4() : subscriber?.data.id.value,
      });
      if (subscriber) {
        chatBotEntity({ subscriberId: subscriber?.data.id.value });
      }
    }
  }, [
    dispatch,
    configuration,
    companySettings,
    isAdmin,
    subscriber,
    isLoadingSubscriber,
  ]);

  useEffect(() => {
    if (configuration?.apiHost) {
      authenticate({});
    }
  }, [configuration, authenticate]);

  useEffect(() => {
    if (admin !== undefined && admin?.data === SUBSCRIBER_LOGIN_RESPONSE_CODE) {
      dispatch(setIsAdmin(false));
    } else if (admin !== undefined) {
      dispatch(setIsAdmin(true));
    }
  }, [admin, dispatch]);

  useEffect(() => {
    if (accountDetails) {
      const mfaEnabled = Boolean(accountDetails?.data?.mfa_configuration);
      const mfaRequired = accountDetails?.data?.mfa_configuration.required;
      const mfaConfigured =
        accountDetails?.data?.mfa_configuration.configured || false;

      dispatch(setMfaEnabled(mfaEnabled));
      dispatch(setMfaRequired(mfaRequired));
      dispatch(setMfaConfigured(mfaConfigured));
    }
  }, [accountDetails, dispatch]);

  useEffect(() => {
    if (
      isAuthenticated &&
      isTermsOfUseAgreementValidInLocalStorage(
        subscriber?.data.id.value as number,
        configuration?.tncLocalStorageThresholdDays ?? DEFAULT_CACHE_THRESHOLD
      )
    ) {
      dispatch(setEnrollmentApiError(t('enrollmentConfigError')));
      dispatch(showNavigationMenus());
      dispatch(setTermsOfUseAccepted());
    } else {
      dispatch(hideNavigationMenus());
    }
  }, [
    dispatch,
    isAuthenticated,
    configuration?.tncLocalStorageThresholdDays,
    subscriber?.data.id.value,
    companySettings?.data.source_license,
    companySettings?.data.lumos_feature_toggle,
    isAdmin,
    t,
  ]);

  useEffect(() => {
    if (
      isAuthenticated &&
      configuration?.firebaseOptions &&
      configuration?.vapidKey
    ) {
      const { firebaseOptions, vapidKey } = configuration;

      getNotificationsPermission({ firebaseOptions, vapidKey });
    }
  }, [isAuthenticated, configuration]);

  const isLoading =
    isLoadingConfiguration ||
    !configuration ||
    isAuthenticating ||
    isAuthenticated === null ||
    isLoadingContentSettings ||
    isFetchingContentSettings ||
    isLoadingManifestData ||
    isLoadingSubscriber ||
    isFetchingSubscriber ||
    isLoadingBenefitsData ||
    isLoadingUserSpecificData ||
    isLoadingAdmin ||
    isFetchingAdmin ||
    isCompanySettingsLoading;

  useEffect(() => {
    isNativeAuthSupported().then((isSupported) =>
      dispatch(setIsSupported(isSupported))
    );
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated && contentSettings?.user_name) {
      dispatch(setUsername(contentSettings.user_name));
    }

    if (
      contentSettings?.cost_period_text &&
      contentSettings.cost_period_text !== 'Per Pay Period'
    ) {
      dispatch(setPerPayPeriodText(contentSettings.cost_period_text));
    } else {
      dispatch(setPerPayPeriodText(t('perPeriod')));
    }
  }, [
    dispatch,
    t,
    isAuthenticated,
    contentSettings?.user_name,
    contentSettings?.cost_period_text,
  ]);

  useEffect(() => {
    if (
      Boolean(pendo?.isReady) &&
      !pendo.isReady() &&
      Boolean(subscriber?.data?.organization_id?.value) &&
      Boolean(contentSettings?.logged_in_user_id)
    ) {
      pendo.initialize({
        visitor: {
          id: contentSettings?.logged_in_user_id, // Required if user is logged in, default creates anonymous ID
        },
        account: {
          id: subscriber?.data?.organization_id?.value, // Required if using Pendo Feedback, default uses the value 'ACCOUNT-UNIQUE-ID'
        },
      });
    }
  }, [
    subscriber?.data?.organization_id?.value,
    contentSettings?.logged_in_user_id,
  ]);

  useEffect(() => {
    const beforeInstallListener = (e: BeforeInstallPromptEvent) => {
      // Prevent Chrome 76 and later from showing the install prompt
      e.preventDefault();
      // Stash the event so it can be triggered later.
      dispatch(setDownloadPrompt(e));
    };
    window.addEventListener('beforeinstallprompt', beforeInstallListener);
  });

  useEffect(() => {
    // TODO: revisit this code while working on animation
    // for now, just hiding chatbot when loader is visible
    if (isLoading) {
      document
        .querySelector('.chat-bot')
        ?.setAttribute('style', 'display: none');
    }
  }, [isLoading]);

  useEffect(() => {
    if (manifestData?.theme_color) {
      const style = document.createElement('style');
      style.innerHTML = `
        .drawer-dialog h1::after {
          background-color: ${manifestData.theme_color};
        }
      `;
      document.head.appendChild(style);
    }
  }, [manifestData?.theme_color]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      let timeoutId: NodeJS.Timeout | null = null;
      const observer = new MutationObserver(() => {
        // Clear any pending timeout
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        // Set a new timeout to run axe after a short delay
        timeoutId = setTimeout(() => {
          const root = document.getElementById('root');
          const loader = document.querySelector('[aria-label="Loading..."]');

          if (root?.children.length && !loader) {
            console.log('Running axe accessibility check...');
            import('@axe-core/react').then((axe) => {
              const ReactDOM = require('react-dom');
              axe.default(React, ReactDOM, 1000);
              console.log('✓ Axe check complete');
            });
          }
        }, 1000);
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      return () => {
        observer.disconnect();
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }
  }, []);

  function decideLandingPage(configuration: ConfigurationResponseDto) {
    // currently source is always required
    if ((sourceEnabled && homeEnabled) || (lumosEnabled && sourceEnabled)) {
      return <HomePage />;
    } else if (sourceEnabled) {
      return (
        <Home
          configuration={configuration}
          lumosEnabled={lumosEnabled}
          sourceEnabled={sourceEnabled}
        />
      );
    } else {
      return <Enrollment />;
    }
  }
  const enableClickToChat = contentSettings?.click_to_chat?.enrollment_enabled;

  return (
    <BrowserRouter basename={`/${companyName}`}>
      <ScrollToTop />
      <ToastDialogProvider>
        <ThemeProvider theme={defaultTheme}>
          <Suspense fallback={<Loader isFullScreen />}>
            <ThemeProvider theme={getTheme(manifestData?.theme_color)}>
              {enableClickToChat && <ClickToChat />}
              <CssBaseline />
              {/* <div>Color?
                {manifestData?.theme_color}
              </div> */}
              <ErrorBoundary>
                {(!isLoading &&
                  !manifestData &&
                  companySettings?.data.source_license) ||
                companyName === '' ? (
                  <PageNotFound isFullScreen />
                ) : (
                  <>
                    <Snackbar />
                    {isLoading ? (
                      <Loader isFullScreen />
                    ) : (
                      <>
                        <PageLayout navigationVisible={navigationVisible}>
                          <Suspense fallback={<Loader />}>
                            <RoutesInternal>
                              <Route
                                path={ROUTES.public.login.path}
                                element={<PublicOutlet />}
                              >
                                <Route index element={<Login />} />
                              </Route>
                              <Route
                                path={ROUTES.public.mfa.path}
                                element={<PublicOutlet />}
                              >
                                <Route index element={<MFA />} />
                              </Route>
                              <Route
                                path={`${ROUTES.public.forgotPassword.path}/*`}
                                element={<ForgotPassword />}
                              />
                              <Route
                                path={ROUTES.public.changePassword.path}
                                element={<PublicOutlet />}
                              >
                                <Route
                                  index
                                  element={
                                    lumosEnabled ? (
                                      <LumosChangePassword />
                                    ) : (
                                      <ChangePassword />
                                    )
                                  }
                                />
                              </Route>
                              <Route
                                path={ROUTES.public.termsAndConditions.path}
                                element={<TermsOfUse />}
                              />
                              <Route
                                path={`${ROUTES.public.termsAndConditions.path}/readonly`}
                                element={<TermsDisplay />}
                              />
                              <Route
                                path={ROUTES.public.legalDocuments.path}
                                element={<LegalDocuments />}
                              />
                              <Route
                                path={`${ROUTES.public.thirdPartyLicenses.path}/*`}
                                element={<LicensesPage />}
                              />
                              <Route
                                path={ROUTES.public.privacyPolicy.path}
                                element={<PrivacyPolicyDisplay />}
                              />
                              <Route
                                path={ROUTES.public.accessDenied.path}
                                element={<AccessDenied />}
                              />
                              {/* New HomePage to replace both current Source and Lumos landing pages*/}
                              {/* <Route path={ROUTES.home.path} element={<HomePage />} /> */}
                              <Route
                                path={ROUTES.main.home.path}
                                element={
                                  <PrivateOutlet
                                    sourceEnabled={Boolean(sourceEnabled)}
                                  />
                                }
                              >
                                {/* we want to replace the homepage with enrollment if non-source org */}
                                <Route
                                  path=""
                                  element={decideLandingPage(configuration)}
                                />
                                <Route
                                  path={`${ROUTES.main.benefits.path}/*`}
                                  element={
                                    <Benefits lumosEnabled={lumosEnabled} />
                                  }
                                />
                                {lumosEnabled && (
                                  <Route
                                    path={`${ROUTES.lifeEvent.path}/*`}
                                    element={<ReportLifeEvent />}
                                  />
                                )}
                                <Route
                                  path={`${ROUTES.main.inbox.path}/*`}
                                  element={<Inbox />}
                                />
                                <Route
                                  path="/info/:slotNum"
                                  element={<BenefitInformation />}
                                />
                                <Route
                                  path="/info/view-all"
                                  element={<ViewAllBenefits />}
                                />
                                <Route
                                  path={`${ROUTES.additional.profile.myProfile.path}/*`}
                                  element={
                                    lumosEnabled ? <MyProfile /> : <Profile />
                                  }
                                />
                                <Route
                                  path={`${ROUTES.additional.profile.myFamily.path}/*`}
                                  element={
                                    lumosEnabled ? (
                                      <MyFamily />
                                    ) : (
                                      <FamilyMembers />
                                    )
                                  }
                                />
                                <Route
                                  path={`${ROUTES.additional.profile.myBeneficiaries.path}/*`}
                                  element={<MyBeneficiaries />}
                                />
                                <Route
                                  path={`${ROUTES.additional.profile.myIdCards.path}/*`}
                                  element={
                                    lumosEnabled ? <MyIdCards /> : <IdCards />
                                  }
                                />
                                <Route
                                  path={`${ROUTES.additional.profile.contacts.path}/*`}
                                  element={
                                    lumosEnabled ? <MyContacts /> : <Contacts />
                                  }
                                />
                                <Route
                                  path={`${ROUTES.additional.documents.acaDocuments.path}/*`}
                                  element={<AcaDocuments />}
                                />
                                <Route
                                  path={`${ROUTES.additional.account.settings.path}/*`}
                                  element={<Settings />}
                                />
                                <Route
                                  path={`${ROUTES.additional.company.myCompany.path}/*`}
                                  element={
                                    lumosEnabled ? (
                                      <MyCompany />
                                    ) : (
                                      <CompanyCategories />
                                    )
                                  }
                                />
                                <Route
                                  path={`${ROUTES.public.thirdPartyLicenses.path}/*`}
                                  element={<LicensesPage />}
                                />
                                <Route
                                  path={ROUTES.toDoList.addYourDocuments.path}
                                  element={<AddYourDocuments />}
                                />
                                <Route
                                  path={ROUTES.toDoList.eoi.path}
                                  element={<EOI />}
                                />
                                <Route
                                  path={`${ROUTES.enrollment.path}/*`}
                                  element={<Enrollment />}
                                />
                                <Route
                                  path={
                                    ROUTES.additional.account
                                      .securityPreferences.path
                                  }
                                  element={<ConfigureMFA />}
                                />
                              </Route>
                            </RoutesInternal>
                          </Suspense>
                        </PageLayout>
                      </>
                    )}
                  </>
                )}
              </ErrorBoundary>
            </ThemeProvider>
          </Suspense>
        </ThemeProvider>
      </ToastDialogProvider>
    </BrowserRouter>
  );
};
